import { useState } from "react";
import { BlockStack, Button, Text } from "@shopify/polaris";

interface Props {
  /** The classified, readable sentence from print_jobs.error. */
  error: string;
  /** The carrier's own words, newline-joined. Null on rows predating the classifier. */
  detail?: string | null;
  /** bodySm on the dense Orders row, body in Activity's expanded panel. */
  variant?: "bodySm" | "bodyMd";
}

// One failure, shown the same way everywhere.
//
// Orders and Activity both used to render `print_jobs.error` raw and in red, so
// EasyPost's per-carrier chatter — "FedExDefault: Only US outbound shipment is
// supported" — was the whole of what a merchant saw. It reads as a crash in
// ShipPromise rather than as a fixable EasyPost account problem, and that
// misreading is what failed App Store review.
//
// The classified sentence leads. The raw lines stay one click away rather than
// gone: when the classifier guesses wrong, they are the only thing that says so,
// and support cannot debug a carrier rejection it cannot see. Extracted into a
// component because the two screens rendering this differently is how one of
// them ends up back on raw text.
export default function FailureNotice({ error, detail, variant = "bodySm" }: Props): JSX.Element {
  const [showDetail, setShowDetail] = useState(false);

  return (
    <BlockStack gap="100">
      <Text as="span" tone="critical" variant={variant} breakWord>
        {error}
      </Text>
      {detail ? (
        <>
          {/* stopPropagation is load-bearing, not defensive. On Orders this
              renders inside an IndexTable.Row, and Polaris binds that row's
              selection handler to the <tr> — neither IndexTable.Cell nor
              Button stops the bubble. Without this, clicking "What the carriers
              said" to READ why an order failed also silently selects the row,
              and the selection feeds "Buy + Print Selected". A merchant
              inspecting a failure would buy postage for it. */}
          <div onClick={(event) => event.stopPropagation()}>
            <Button
              variant="plain"
              size="micro"
              disclosure={showDetail ? "up" : "down"}
              onClick={() => setShowDetail(!showDetail)}
            >
              {showDetail ? "Hide carrier response" : "What the carriers said"}
            </Button>
          </div>
          {showDetail ? (
            // Preserved as written: the newlines separate one carrier's verdict
            // from the next, and collapsing them runs two different rejections
            // into a single unreadable sentence.
            <Text as="p" tone="subdued" variant="bodySm" breakWord>
              <span style={{ whiteSpace: "pre-wrap" }}>{detail}</span>
            </Text>
          ) : null}
        </>
      ) : null}
    </BlockStack>
  );
}
